import { useState } from 'react';
import styled from 'styled-components';
import { StatsFilterModal } from './modal/StatsFilterModal';
import { useDragScroll } from '../../../utils/useDragScroll';

type FilterState = {
  period: string;
  range: string;
  count: string;
  special: string;
};

type StatsFilterProps = {
  filter: FilterState;
  setFilter: (val: FilterState) => void;
  showSpecial?: boolean;
  isPremium: boolean;
};

const periodMapping: Record<string, string> = {
  '3개월 이내': '3',
  '1년 이내': '12',
  '3년 이내': '36',
  '5년 이내': '60',
  '10년 이내': '120',
};

const rangeMapping: Record<string, string> = {
  '주변 500m': '500',
  '주변 1km': '1000',
  '주변 3km': '3000',
};

const countMapping: Record<string, string> = {
  '5개': '5',
  '10개': '10',
  '20개': '20',
};

const defaultLabels = {
  period: '3년 이내',
  range: '주변 3km',
  count: '5개',
};

const defaultValues = {
  period: '36',
  range: '3000',
  count: '5',
};

const StatsFilter = ({
  filter,
  setFilter,
  showSpecial = true,
  isPremium = false,
}: StatsFilterProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [periodLabel, setPeriodLabel] = useState(defaultLabels.period);
  const [rangeLabel, setRangeLabel] = useState(defaultLabels.range);
  const [countLabel, setCountLabel] = useState(defaultLabels.count);

  const [period, setPeriod] = useState(defaultValues.period);
  const [range, setRange] = useState(defaultValues.range);
  const [count, setCount] = useState(defaultValues.count);

  const [special, setSpecial] = useState('');

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const buttons = [
    {
      label: '기간',
      value: period,
      display: periodLabel,
      setLabel: setPeriodLabel,
      setValue: setPeriod,
      mapping: periodMapping,
    },
    {
      label: '범위',
      value: range,
      display: rangeLabel,
      setLabel: setRangeLabel,
      setValue: setRange,
      mapping: rangeMapping,
    },
    {
      label: '검색갯수',
      value: count,
      display: countLabel,
      setLabel: setCountLabel,
      setValue: setCount,
      mapping: countMapping,
    },
  ];

  const specialText =
    !special || special === 'none'
      ? '없음'
      : `${special.split(',').length - 1}개`;

  const handleModalChange = (val: {
    selectedOptions: string[];
    specialValue: string;
  }) => {
    const flatOptions = val.selectedOptions.flatMap((item) =>
      Array.isArray(item) ? item : [item],
    );
    const [newPeriod, newRange, newCount] = flatOptions;

    const updatedFilter = {
      period: newPeriod ? periodMapping[newPeriod] : defaultValues.period,
      range: newRange ? rangeMapping[newRange] : defaultValues.range,
      count: newCount ? countMapping[newCount] : defaultValues.count,
      special: val.specialValue || '',
    };

    setPeriodLabel(newPeriod || defaultLabels.period);
    setRangeLabel(newRange || defaultLabels.range);
    setCountLabel(newCount || defaultLabels.count);

    setPeriod(updatedFilter.period);
    setRange(updatedFilter.range);
    setCount(updatedFilter.count);

    const specialValue =
      updatedFilter.special === 'all' || updatedFilter.special === 'any'
        ? ''
        : updatedFilter.special;

    console.log('updatedFilter.special', specialValue);
    setSpecial(specialValue);

    setFilter(updatedFilter);
  };

  return (
    <Container>
      <RowLeft
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
      >
        {buttons.map((btn) => (
          <ControlGroup key={btn.label}>
            <SpecialButton
              value={btn.value}
              onClick={() => isPremium && setIsModalOpen(true)}
              disabled={!isPremium}
            >
              {btn.label} <span>{btn.display}</span>
            </SpecialButton>
          </ControlGroup>
        ))}

        {showSpecial && (
          <>
            <SpecialButton
              onClick={() => isPremium && setIsModalOpen(true)}
              disabled={!isPremium}
            >
              특수 조건 <span>{specialText}</span>
            </SpecialButton>
          </>
        )}
        <StatsFilterModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          auction={showSpecial}
          onChangeOption={handleModalChange}
        />
      </RowLeft>
    </Container>
  );
};

export default StatsFilter;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 16px;
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  overflow-x: auto;

  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpecialButton = styled.button`
  font-weight: 500;
  font-size: 14px;
  color: #787d87;
  border: 1px solid #e8e9ed;
  cursor: pointer;
  height: 36px;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding-right: 18px;
  padding-left: 18px;
  border-radius: 999px;
  border-width: 1px;
  background-color: #fff;

  &:hover {
    background-color: #f4f5f7;
  }

  &:active {
    background-color: #e8e9ed;
  }

  span {
    margin-left: 10px;
    color: #000;
  }
`;
